// Consent for Google Ads conversion measurement (operator, 06.10.2026) and,
// since 11.10.2026, for Google Analytics statistics. The Google tag loads only
// after an explicit "accept" (TDDDG § 25, Art. 6 (1) (a) GDPR); "reject" and
// "no decision yet" load nothing. The old key "dw.consent.analytics" only
// acknowledged a notice and is not consent, so statistics use a new key.

import { GA4_ID } from "@/lib/config";

export const ADS_CONSENT_KEY = "dw.consent.ads";
export const STATS_CONSENT_KEY = "dw.consent.stats";
export const CONSENT_EVENT = "dw-consent";
export type AdsConsent = "granted" | "denied" | null;

function read(key: string): AdsConsent {
  try {
    const v = localStorage.getItem(key);
    return v === "granted" || v === "denied" ? v : null;
  } catch {
    return null;
  }
}

function write(key: string, v: "granted" | "denied" | null): void {
  try {
    if (v) localStorage.setItem(key, v);
    else localStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

export function getAdsConsent(): AdsConsent {
  return read(ADS_CONSENT_KEY);
}

/** Statistics (Google Analytics); "denied" while no GA4 property is set up. */
export function getStatsConsent(): AdsConsent {
  return GA4_ID ? read(STATS_CONSENT_KEY) : "denied";
}

/** True once the visitor decided, whatever the answer. */
export function hasConsentDecision(): boolean {
  return getAdsConsent() !== null && (!GA4_ID || getStatsConsent() !== null);
}

export function setConsent(choice: { ads: "granted" | "denied" | null; stats?: "granted" | "denied" | null }): void {
  write(ADS_CONSENT_KEY, choice.ads);
  if (GA4_ID) write(STATS_CONSENT_KEY, choice.stats ?? choice.ads);
  window.dispatchEvent(new Event(CONSENT_EVENT));
}

/** Kept for older callers: one answer for every purpose. */
export function setAdsConsent(v: "granted" | "denied" | null): void {
  setConsent({ ads: v, stats: v });
}

/** Set by "Cookie-Einstellungen": after the reload the banner shows at once. */
/** Portal, remainder payment and admin: personal pages with case numbers and
 * signed links in the address. No Google tag and no banner there (process
 * test 18.10.2026: gtag sent portal URLs with case ids to Google). */
export const PRIVATE_AREA = /^(?:\/(?:en|da|ar|uk|ru|vi|es))?\/(?:status|restzahlung|admin)(?:\/|$)/;

export const CONSENT_REOPEN_KEY = "dw.consent.reopen";

/**
 * Withdraw or change the choice (Art. 7 (3) GDPR: as easy as giving it).
 * Clears the choice, deletes the cookies Google set (_gcl_* for Ads, _ga* for
 * Analytics) and reloads, so a loaded Google tag is gone and the banner asks
 * again at once.
 */
export function reopenConsent(): void {
  setConsent({ ads: null, stats: null });
  try {
    const host = window.location.hostname;
    const domains = ["", host, `.${host}`, `.${host.replace(/^www\./, "")}`];
    for (const c of document.cookie.split(";")) {
      const name = c.split("=")[0].trim();
      if (!/^_gcl_|^_gac_|^_ga$|^_ga_/.test(name)) continue;
      for (const d of domains) document.cookie = `${name}=; Max-Age=0; path=/${d ? `; domain=${d}` : ""}`;
    }
    sessionStorage.setItem(CONSENT_REOPEN_KEY, "1");
  } catch {
    /* private mode */
  }
  window.location.reload();
}
